import React, { useState, useRef, useEffect, useCallback } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useTranslation } from 'react-i18next';
import { apiCall, API_BASE } from '../../api';
import { setI18nLanguage } from '../../i18n/config';
import i18n from '../../i18n/config';

function Contributions({
  ngo,
  onAddContribution,
  onBack,
  onHome,
  onEditProfile,
}) {
  const { user, logout, getAccessTokenSilently } = useAuth0();
  const { t } = useTranslation('contributions');
  const { t: tc } = useTranslation('common');

  // Category state
  const [categories, setCategories] = useState([]);

  // Filter state
  const [minAmount, setMinAmount] = useState("");
  const [maxAmount, setMaxAmount] = useState("");
  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [search, setSearch] = useState("");

  // Data state
  const [contributions, setContributions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // ---- Profile dropdown state ----
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef(null);

  // ---- Language menu state ----
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const langMenuRef = useRef(null);

  useEffect(() => {
    if (!profileOpen) return;
    const handleClickOutside = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [profileOpen]);

  // Close the language menu on outside click.
  useEffect(() => {
    if (!langMenuOpen) return;
    const handleClickOutside = (e) => {
      if (langMenuRef.current && !langMenuRef.current.contains(e.target)) {
        setLangMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [langMenuOpen]);

  // Fetch categories
  useEffect(() => {
    let cancelled = false;
    const fetchCategories = async () => {
      try {
        const data = await apiCall("/api/contributions/categories/", {}, getAccessTokenSilently);
        if (!cancelled && data.results) {
          setCategories(data.results);
        }
      } catch (err) {
        console.error("Failed to load categories", err);
      }
    };
    fetchCategories();
    return () => { cancelled = true; };
  }, [getAccessTokenSilently]);

  const fetchContributions = useCallback(async (filters = {}) => {
    if (!ngo?.id) return;
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams({ org_id: ngo.id });
      if (filters.search) params.append("search", filters.search);
      if (filters.fromDate) params.append("date_from", filters.fromDate);
      if (filters.toDate) params.append("date_to", filters.toDate);
      if (filters.minAmount) params.append("amount_min", filters.minAmount);
      if (filters.maxAmount) params.append("amount_max", filters.maxAmount);
      if (filters.selectedCategoryId) params.append("category_id", filters.selectedCategoryId);

      const data = await apiCall(`/api/contributions/?${params.toString()}`, {}, getAccessTokenSilently);
      setContributions(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(tc('error'));
    } finally {
      setLoading(false);
    }
  }, [ngo?.id, getAccessTokenSilently,tc]);

  // Initial fetch
  useEffect(() => {
    fetchContributions({
      search, fromDate, toDate, minAmount, maxAmount, selectedCategoryId
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ngo?.id, getAccessTokenSilently]); // only run initially (or on ngo change)

  const handleLogout = () => {
    setProfileOpen(false);
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  const handleEditProfile = () => {
    setProfileOpen(false);
    if (onEditProfile) onEditProfile();
  };

  const handleApply = () => {
    fetchContributions({ search, fromDate, toDate, minAmount, maxAmount, selectedCategoryId });
  };

  const handleReset = () => {
    setSearch("");
    setFromDate("");
    setToDate("");
    setMinAmount("");
    setMaxAmount("");
    setSelectedCategoryId("");
    fetchContributions({});
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top bar */}
      <div className="bg-white border-b border-gray-200 px-4 py-4 flex items-center gap-3">
        {/* Home icon */}
        <button onClick={onHome} className="text-blue-600 hover:text-blue-700 flex-shrink-0" aria-label="Home" title="Home">
          <svg width="20" height="20" viewBox="0 0 24 24" className="fill-current">
            <path d="M12 3.2 3 10v10h6v-6h6v6h6V10l-9-6.8Z" />
          </svg>
        </button>

        {/* Back arrow */}
        <button onClick={onBack} aria-label="Back" className="text-blue-600 hover:text-blue-700">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <path
              d="M19 12H5M5 12l7-7M5 12l7 7"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>

        {/* Top-bar label */}
        <span className="text-sm font-medium text-gray-900 flex-1">{t('title')}</span>

        <div className="flex-1" />

        {/* ---- LANGUAGE TOGGLE ---- */}
        <div ref={langMenuRef} className="relative">
          <button
            onClick={() => setLangMenuOpen((open) => !open)}
            aria-label="Language menu"
            aria-expanded={langMenuOpen}
            className="text-xs font-medium text-gray-600 border border-gray-200 rounded-lg px-2.5 py-1.5 hover:bg-gray-50 transition"
          >
            {i18n.language === 'ml' ? 'മലയാളം' : 'EN'}
          </button>
          {langMenuOpen && (
            <div className="absolute right-0 top-10 w-32 bg-white rounded-xl shadow-lg border border-gray-100 p-2 z-50">
              <button
                onClick={() => {
                  const lang = 'en';
                  setI18nLanguage(lang);
                  localStorage.setItem('zbookkeeping_language', lang);
                  setLangMenuOpen(false);
                  // Persist to backend (fire-and-forget)
                  getAccessTokenSilently({
                    authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE },
                  }).then(token => {
                    fetch(`${API_BASE || ''}/api/accounts/profile/update/`, {
                      method: 'PATCH',
                      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                      body: JSON.stringify({ preferred_language: lang }),
                    }).catch(() => {});
                  }).catch(() => {});
                }}
                className={`w-full text-left rounded-lg px-3 py-2 text-sm transition ${
                  i18n.language === 'en' ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                English
              </button>
              <button
                onClick={() => {
                  const lang = 'ml';
                  setI18nLanguage(lang);
                  localStorage.setItem('zbookkeeping_language', lang);
                  setLangMenuOpen(false);
                  // Persist to backend (fire-and-forget)
                  getAccessTokenSilently({
                    authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE },
                  }).then(token => {
                    fetch(`${API_BASE || ''}/api/accounts/profile/update/`, {
                      method: 'PATCH',
                      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                      body: JSON.stringify({ preferred_language: lang }),
                    }).catch(() => {});
                  }).catch(() => {});
                }}
                className={`w-full text-left rounded-lg px-3 py-2 text-sm transition ${
                  i18n.language === 'ml' ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                മലയാളം
              </button>
            </div>
          )}
        </div>

        {/* Profile avatar button + dropdown */}
        <div ref={profileRef} className="relative">
          <button
            onClick={() => setProfileOpen((open) => !open)}
            aria-label="Account menu"
            aria-expanded={profileOpen}
            className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-xs font-medium"
          >
            {user?.name?.charAt(0).toUpperCase() || 'U'}
          </button>

          {profileOpen && (
            <div className="absolute right-0 top-11 w-64 bg-white rounded-xl shadow-lg border border-gray-100 p-4 z-50">
              <p className="text-sm text-gray-900 truncate">{user?.email}</p>
              <p className="text-sm text-gray-400 truncate mb-4">{user?.email}</p>

              <button
                onClick={handleEditProfile}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-lg py-2.5 text-sm font-medium transition mb-2"
              >
                {tc('editProfile')}
              </button>
              <button
                onClick={handleLogout}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-lg py-2.5 text-sm font-medium transition"
              >
                {tc('logout')}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Page content */}
      <div className="max-w-sm mx-auto px-4 py-8">

        {/* Body heading — §2 */}
        <h1 className="text-center text-base font-medium text-blue-600 mb-6 uppercase tracking-wider">
          {t('title')}
        </h1>

        {/* Add Contribution Button */}
        <button
          type="button"
          onClick={onAddContribution}
          className="mb-4 w-full bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-4 py-3 text-sm font-medium transition"
        >
          {t('addButton')}
        </button>

        {/* Search */}
        <div className="mb-4">
          <p className="text-xs text-gray-500 uppercase tracking-widest mb-1.5">
            {t('searchLabel')}
          </p>

          <input
            type="text"
            placeholder={tc('searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-gray-100 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400 border-0"
          />
        </div>

        {/* Filters */}
        <p className="text-xs text-gray-500 uppercase tracking-widest mb-1.5">
          {t('filters')}
        </p>

        <div className="rounded-2xl bg-white border border-gray-200 px-4 py-4">
          {/* Date */}
          <div className="mb-4">
            <p className="text-xs text-gray-500 uppercase tracking-widest mb-1.5">
              {t('dateLabel')}
            </p>

            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="mb-2 w-full bg-gray-100 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400 border-0"
            />

            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="w-full bg-gray-100 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400 border-0"
            />
          </div>

          {/* Category */}
          <div className="mb-4">
            <p className="text-xs text-gray-500 uppercase tracking-widest mb-1.5">
              {t('categoryLabel')}
            </p>

            <div className="flex flex-wrap gap-2">
              {categories.map((category) => (
                <button
                  key={category.id}
                  type="button"
                  onClick={() => setSelectedCategoryId(category.id)}
                  className={`rounded-lg px-3 py-2.5 text-sm transition ${
                    selectedCategoryId === category.id
                      ? "bg-blue-100 text-blue-700 font-medium"
                      : "bg-gray-100 text-gray-700"
                  }`}
                >
                  {category.name}
                </button>
              ))}
            </div>
          </div>

          {/* Amount */}
          <div className="mb-4">
            <p className="text-xs text-gray-500 uppercase tracking-widest mb-1.5">
              {t('amountRange')}
            </p>

            <div className="grid grid-cols-2 gap-2">
              <input
                type="number"
                placeholder="Min"
                value={minAmount}
                onChange={(e) => setMinAmount(e.target.value)}
                className="bg-gray-100 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400 border-0"
              />

              <input
                type="number"
                placeholder="Max"
                value={maxAmount}
                onChange={(e) => setMaxAmount(e.target.value)}
                className="bg-gray-100 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400 border-0"
              />
            </div>
          </div>

          {/* Buttons */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleApply}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-4 py-2.5 text-sm font-medium transition"
            >
              {tc('apply')}
            </button>

            <button
              type="button"
              onClick={handleReset}
              className="flex-1 bg-red-500 hover:bg-red-600 text-white rounded-lg py-2 text-xs font-medium transition"
            >
              {tc('reset')}
            </button>
          </div>
        </div>

        {/* Contributions List */}
        <p className="mt-6 text-center text-xs text-gray-500 uppercase tracking-widest mb-3">
          {t('listTitle')}
        </p>

        <div className="flex flex-col gap-3">
          {loading && (
            <p className="text-center text-gray-400 text-sm">{t('loading')}</p>
          )}
          {!loading && error && (
            <p className="text-center text-red-600 text-sm">{error}</p>
          )}
          {!loading && !error && contributions.length === 0 && (
            <p className="text-center text-gray-400 text-sm">{t('noData')}</p>
          )}
          {!loading && !error && contributions.map(c => (
            <div key={c.id} className="bg-white border border-gray-200 rounded-xl p-4 flex justify-between items-center">
              <div>
                <p className="text-sm font-semibold text-gray-900 m-0">{c.donor_name || t('unknownDonor')}</p>
                <p className="text-xs text-gray-500 mt-1">{c.date} • {c.payment_method_display || c.payment_method}</p>
              </div>
              <p className="text-sm font-bold text-green-600 m-0">
                +₹{c.amount}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default Contributions;